import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/shared/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      { path: 'login', name: 'login', component: () => import('@/pages/auth/LoginPage.vue') },
      { path: 'activate', name: 'activate', component: () => import('@/pages/auth/ActivatePage.vue') },
      { path: 'forgot-password', name: 'forgot-password', component: () => import('@/pages/auth/ForgotPasswordPage.vue') },
      { path: 'reset-password', name: 'reset-password', component: () => import('@/pages/auth/ResetPasswordPage.vue') },
    ],
  },
  // Redirige l'ancien chemin /login pour ne pas casser les liens existants
  { path: '/login', redirect: { name: 'login' } },
  {
    path: '/',
    component: () => import('@/layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'dashboard', component: () => import('@/pages/dashboard/DashboardPage.vue'), meta: { perm: ['analytics', 'view'] } },
      { path: 'companies', name: 'companies', component: () => import('@/pages/companies/CompaniesPage.vue'), meta: { perm: ['companies', 'view'] } },
      { path: 'companies/:id', name: 'company-detail', component: () => import('@/pages/companies/CompanyDetailPage.vue'), meta: { perm: ['companies', 'view'] } },
      { path: 'modules-catalog', name: 'modules-catalog', component: () => import('@/pages/modules-catalog/ModulesCatalogPage.vue'), meta: { perm: ['modules_catalog', 'view'] } },
      { path: 'platform-users', name: 'platform-users', component: () => import('@/pages/platform-users/PlatformUsersPage.vue'), meta: { perm: ['platform_users', 'view'] } },
      { path: 'platform-roles', name: 'platform-roles', component: () => import('@/pages/platform-roles/PlatformRolesPage.vue'), meta: { perm: ['platform_roles', 'view'] } },
      { path: 'platform-roles/:id', name: 'role-edit', component: () => import('@/pages/platform-roles/PlatformRoleEditorPage.vue'), meta: { perm: ['platform_roles', 'view'] } },
      { path: 'profile', name: 'profile', component: () => import('@/pages/profile/ProfilePage.vue') },
      { path: 'plans', name: 'plans', component: () => import('@/pages/plans/PlansPage.vue'), meta: { perm: ['plans', 'view'] } },
      { path: 'discounts', name: 'discounts', component: () => import('@/pages/discounts/DiscountsPage.vue'), meta: { perm: ['pricing', 'view'] } },
      { path: 'subscriptions', name: 'subscriptions', component: () => import('@/pages/subscriptions/SubscriptionsPage.vue'), meta: { perm: ['subscriptions', 'view'] } },
      { path: 'invoices', name: 'invoices', component: () => import('@/pages/invoices/InvoicesPage.vue'), meta: { perm: ['invoices', 'view'] } },
      { path: 'refunds', name: 'refunds', component: () => import('@/pages/refunds/RefundRequestsPage.vue'), meta: { perm: ['payments', 'view'] } },
      { path: 'platform-support', name: 'platform-support', component: () => import('@/pages/platform-support/PlatformSupportPage.vue'), meta: { perm: ['support', 'view'] } },
      { path: 'platform-support/config', name: 'platform-support-config', component: () => import('@/pages/platform-support/PlatformSupportConfigPage.vue'), meta: { perm: ['support', 'view'] } },
      { path: 'platform-support/:id', name: 'platform-support-detail', component: () => import('@/pages/platform-support/PlatformSupportDetailPage.vue'), meta: { perm: ['support', 'view'] } },
      { path: 'settings', name: 'settings', component: () => import('@/pages/settings/SettingsPage.vue'), meta: { perm: ['settings', 'view'] } },
      { path: 'exchange-rates', name: 'exchange-rates', component: () => import('@/pages/exchange-rates/ExchangeRatesPage.vue'), meta: { perm: ['settings', 'view'] } },
      { path: 'universal-roles', name: 'universal-roles', component: () => import('@/pages/universal-roles/UniversalRolesPage.vue'), meta: { perm: ['settings', 'view'] } },
      { path: 'audit-logs', name: 'audit-logs', component: () => import('@/pages/audit-logs/AuditLogsPage.vue'), meta: { perm: ['audit_logs', 'view'] } },
      { path: 'forbidden', name: 'forbidden', component: () => import('@/pages/errors/ForbiddenPage.vue') },
      { path: 'not-found', name: 'not-found', component: () => import('@/pages/errors/NotFoundPage.vue') },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: { name: 'not-found' } },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Routes publiques (onboarding, reset password) : le guard ne doit pas
// appeler /me ni forcer une redirection basee sur la session — sinon les
// liens d'invitation/reset envoyes par mail deviennent inaccessibles.
const PUBLIC_AUTH_ROUTES = new Set(['activate', 'forgot-password', 'reset-password'])

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  const isPublicAuthRoute = typeof to.name === 'string' && PUBLIC_AUTH_ROUTES.has(to.name)

  if (isPublicAuthRoute) {
    return
  }

  if (!auth.user) {
    await auth.fetchMe()
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.path.startsWith('/auth') && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }

  // Check route-level permission
  if (auth.isAuthenticated) {
    const requiredPerm = to.matched
      .map(r => r.meta?.perm as [string, string] | undefined)
      .filter((p): p is [string, string] => Array.isArray(p) && p.length === 2)

    for (const [module, action] of requiredPerm) {
      if (!auth.can(module, action)) {
        return { name: 'forbidden' }
      }
    }
  }
})

export default router
